import { useMsal } from "@azure/msal-react";
import { ActionIcon, Button, Group, Modal, Stack, Text, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCheck, IconClock, IconInfoCircle, IconUserPlus } from "@tabler/icons-react";
import { type MRT_ColumnDef, MRT_Table, useMantineReactTable } from "mantine-react-table";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { RawDatasource } from "src/db";
import { fetchPendingRequests } from "src/hooks/useFetchPendingRequests";
import DatasourceRequestAccessModal from "./DatasourceRequestAccessModal";

const ACCESS_STATUS = {
    GRANTED: "Granted",
    PENDING: "Pending",
    REQUEST: "Request",
} as const;

type AccessStatus = (typeof ACCESS_STATUS)[keyof typeof ACCESS_STATUS];

type DatasourceRow = RawDatasource & { access_status: AccessStatus };

function formatRefreshDate(value?: string): string {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function getAccessStatus(row: RawDatasource, pendingRequests: Set<string>): AccessStatus {
    if (row.has_access) return ACCESS_STATUS.GRANTED;
    if (pendingRequests.has(row.id)) return ACCESS_STATUS.PENDING;
    return ACCESS_STATUS.REQUEST;
}

function getColumns(): MRT_ColumnDef<DatasourceRow>[] {
    return [
        {
            id: "access_status",
            accessorKey: "access_status",
            header: "Access",
            enableSorting: false,
            filterVariant: "select",
            filterFn: "equals",
            size: 130,
            grow: false,
            mantineFilterSelectProps: {
                data: [ACCESS_STATUS.GRANTED, ACCESS_STATUS.PENDING, ACCESS_STATUS.REQUEST],
                clearable: true,
                placeholder: "Filter",
            },
            Cell: ({ row, table }) => {
                const [
                    accessRequestModalOpened,
                    { open: openAccessRequest, close: closeAccessRequest },
                ] = useDisclosure(false);
                const modalProps = table.options.meta as {
                    pendingRequests: Set<string>;
                    setPendingRequests: React.Dispatch<React.SetStateAction<Set<string>>>;
                };
                const isPending = modalProps?.pendingRequests?.has(row.original.id);

                if (row.original.has_access) {
                    return (
                        <Tooltip label="Access granted">
                            <div>
                                <Button
                                    aria-label="Access granted"
                                    size="xs"
                                    // compact
                                    style={{
                                        padding: "4px 8px",
                                        backgroundColor: "#107C10",
                                        border: "none",
                                    }}
                                    disabled
                                >
                                    <IconCheck size={12} style={{ color: "white" }} />
                                </Button>
                            </div>
                        </Tooltip>
                    );
                }

                if (isPending) {
                    return (
                        <Tooltip label="Access request pending">
                            <div>
                                <Button
                                    aria-label="Access request pending"
                                    size="xs"
                                    // compact
                                    style={{
                                        padding: "4px 8px",
                                        backgroundColor: "#f59e0b",
                                        border: "none",
                                    }}
                                    disabled
                                >
                                    <IconClock size={12} style={{ color: "white" }} />
                                </Button>
                            </div>
                        </Tooltip>
                    );
                }

                return (
                    <>
                        <Tooltip label="Request access to this knowledge base">
                            <Button
                                aria-label="Request access"
                                size="xs"
                                // compact
                                onClick={openAccessRequest}
                                style={{ padding: "4px 8px" }}
                            >
                                <IconUserPlus size={12} />
                            </Button>
                        </Tooltip>
                        <DatasourceRequestAccessModal
                            opened={accessRequestModalOpened}
                            onClose={() => {
                                closeAccessRequest();
                            }}
                            onSubmit={() => {
                                modalProps?.setPendingRequests(
                                    (prev: Set<string>) => new Set([...prev, row.original.id]),
                                );
                            }}
                            title={`Request Access to ${row.original.label || row.original.id}`}
                            description={row.original.description}
                            ingestion_status={row.original.ingestion_status}
                            requested_datasources={[row.original.id]}
                        />
                    </>
                );
            },
        },
        {
            id: "datasource_name",
            accessorFn: (row: DatasourceRow) => row.label ?? row.id,
            header: "Knowledge Base",
            filterFn: "includesString",
            Cell: ({ row }) => (
                <Group gap={4} wrap="nowrap">
                    <Text size="sm">{row.original.label ?? row.original.id}</Text>
                    <Tooltip label="Show ingestion status and description">
                        <ActionIcon
                            aria-label="Show ingestion status and description"
                            size="xs"
                            variant="subtle"
                            color="gray"
                            onClick={() => row.toggleExpanded()}
                        >
                            <IconInfoCircle size={14} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            ),
        },
        {
            id: "product_lines",
            accessorFn: (row: DatasourceRow) => {
                return (row.product_lines ?? []).toSorted((a, b) => a.localeCompare(b)).join(", ");
            },
            header: "Product Lines",
            enableSorting: false,
            filterFn: "includesString",
        },
        {
            id: "total_count",
            accessorFn: (row: DatasourceRow) => {
                const count = Number(row.total_count);
                return Number.isFinite(count) && count > 0 ? count : 0;
            },
            header: "Count",
            size: 80,
            grow: false,
            sortingFn: "basic",
            enableColumnFilter: false,
            Cell: ({ row }) => {
                const count = Number(row.original.total_count);
                if (!Number.isFinite(count)) return row.original.total_count;
                return count > 0 ? count.toLocaleString() : "";
            },
        },
        {
            id: "latest_document_timestamp",
            header: "Latest Refresh Date",
            accessorFn: (row: DatasourceRow) => formatRefreshDate(row.latest_document_timestamp),
            sortingFn: (rowA, rowB) => {
                const a = rowA.original.latest_document_timestamp
                    ? new Date(rowA.original.latest_document_timestamp).getTime()
                    : 0;
                const b = rowB.original.latest_document_timestamp
                    ? new Date(rowB.original.latest_document_timestamp).getTime()
                    : 0;
                return a - b;
            },
            enableColumnFilter: false,
            Cell: ({ cell }) => (cell.getValue() as string) || "",
        },
    ];
}

interface IngestionStatusOverviewModalProps {
    status?: RawDatasource[];
    opened: boolean;
    fullScreen: boolean;
    onClose: () => void;
}

export const IngestionStatusOverviewModal: React.FC<IngestionStatusOverviewModalProps> = ({
    status,
    opened,
    fullScreen,
    onClose,
}) => {
    const { instance } = useMsal();
    const [pendingRequests, setPendingRequests] = useState<Set<string>>(new Set());

    const tableData = useMemo<DatasourceRow[]>(() => {
        return (status ?? [])
            .toSorted((a, b) => a.id.localeCompare(b.id, "en"))
            .map((ds) => ({
                ...ds,
                access_status: getAccessStatus(ds, pendingRequests),
            }));
    }, [status, pendingRequests]);

    useEffect(() => {
        if (opened) {
            const fetchRequests = async () => {
                try {
                    setPendingRequests(await fetchPendingRequests(instance));
                } catch (err) {
                    console.error("Failed to fetch pending requests:", err);
                }
            };
            fetchRequests();
        }
    }, [instance, opened]);

    const table = useMantineReactTable({
        layoutMode: "grid",
        columns: getColumns(),
        data: tableData,
        meta: { pendingRequests, setPendingRequests },
        enablePagination: false,
        enableColumnActions: false,
        enableColumnFilters: true,
        enableExpanding: true,
        renderDetailPanel: ({ row }) => (
            <Stack gap={2} py={4}>
                <Text size="sm">
                    <strong>Description:</strong> {row.original.description}
                </Text>
                <Text size="sm">
                    <strong>Ingestion Status:</strong> {row.original.ingestion_status}
                </Text>
            </Stack>
        ),
        initialState: {
            density: "xs",
            showColumnFilters: true,
            sorting: [{ id: "datasource_name", desc: false }],
            columnVisibility: { "mrt-row-expand": false },
        },
        mantineTableHeadCellProps: {
            style: {
                justifyContent: "flex-start",
                verticalAlign: "top",
            },
        },
        mantineTableBodyCellProps: { style: { wordWrap: "break-word" } },
    });

    const filteredDocumentSum = table.getFilteredRowModel().rows.reduce((sum, row) => {
        const count = Number(row.original.total_count);
        return sum + (Number.isFinite(count) && count > 0 ? count : 0);
    }, 0);

    return (
        <Modal
            fullScreen={fullScreen}
            closeButtonProps={{ "aria-label": "Close modal" }}
            size="100em"
            opened={opened}
            onClose={onClose}
            title={"Knowledge Base Details"}
        >
            <Stack gap="sm">
                <div className="datasource-details-table">
                    <style>
                        {`
                            .datasource-details-table .mrt-table-head-cell-content {
                                height: auto;
                                align-self: flex-start;
                            }
                        `}
                    </style>
                    <MRT_Table table={table} />
                </div>
                <Group justify="flex-end">
                    <Text size="sm" fw={600}>
                        Total documents: {filteredDocumentSum.toLocaleString()}
                    </Text>
                </Group>
            </Stack>
        </Modal>
    );
};
